import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import commonService from "../common/commonService";
import { API_URLS, STATUS_CODES } from "../common/constants";
import { toast } from "react-toastify";
import { startLoading, stopLoading } from "./commonSlice";

const initialState = {
  errorMessage: "",
  error: null,
  isAuthenticated: false,
  userDetails: null,
  passwordChanged: false,
};

const usernameUpgradeMessage =
  "Please use your app username, not your phone number. Create or confirm your username and password in the app or on the website, then return to LMA.";

const loginErrorMessages = {
  MSISDN_OWNERSHIP_CONFLICT:
    "Your phone number appears to be linked to a different account. Please contact your employer or LMA support to resolve this.",
  EMPLOYEENUMBER_PASSWORD_NOT_MATCH:
    "Incorrect username or password. Please try again.",
  INVALID_USERNAME_OR_PASSWORD:
    "Incorrect username or password. Please try again.",
  PHONE_NUMBER_USERNAME_NOT_ALLOWED: usernameUpgradeMessage,
  USERNAME_UPGRADE_REQUIRED: usernameUpgradeMessage,
  USERNAME_REQUIRED:
    "Please enter your username.",
  NOVA_LOGIN_NOT_CONFIGURED:
    "Login is temporarily unavailable. Please contact LMA support.",
  NO_ACTIVE_SUBSCRIPTION:
    "No active LMA subscription found for this number. Please contact your employer.",
};

export const loginUser = createAsyncThunk(
  "auth/login",
  async (data, { rejectWithValue, dispatch }) => {
    dispatch(startLoading());
    const res = await commonService.noTokenFormDataPostRequest(
      API_URLS.LOGIN,
      data
    );
    dispatch(stopLoading());
    if (res.statusCode !== STATUS_CODES.SUCCESS) {
      const displayMessage = loginErrorMessages[res.message] || res.message;
      toast.error(displayMessage);
      // Reject with both the stable error code and the human display string —
      // downstream UI logic can branch on `code` (e.g. MSISDN_OWNERSHIP_CONFLICT)
      // while components render `message`.
      return rejectWithValue({ code: res.message, message: displayMessage });
    }
    toast.success(res.message);
    return res.data;
  }
);

export const logoutUser = createAsyncThunk(
  "auth/logout",
  async (data, { rejectWithValue, dispatch }) => {
    dispatch(startLoading());
    const res = await commonService.getRequest(API_URLS.LOGOUT);
    dispatch(stopLoading());
    if (res.statusCode !== STATUS_CODES.SUCCESS) {
      toast.error(res.message);
      return rejectWithValue(res.message);
    }
    toast.success(res.message);
    return res.data;
  }
);

const authSlice = createSlice({
  name: "data",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        return {
          ...state,
          error: null,
        };
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        return {
          ...state,
          error: false,
          isAuthenticated: true,
          userDetails: action.payload,
        };
      })
      .addCase(loginUser.rejected, (state, action) => {
        return {
          ...state,
          error: action.payload,
          isAuthenticated: false,
        };
      })
      .addCase(logoutUser.pending, (state) => {
        return {
          ...state,
          error: null,
        };
      })
      .addCase(logoutUser.fulfilled, (state, action) => {
        return {
          ...state,
          error: false,
          isAuthenticated: false,
          userDetails: action.payload,
        };
      })
      .addCase(logoutUser.rejected, (state, action) => {
        return {
          ...state,
          error: action.payload,
          isAuthenticated: true,
        };
      });
  },
});

const { reducer } = authSlice;
export default reducer;
